import { useEffect } from "react";
import { useAuthStore } from "@/features/auth/store/auth.store";
import { connectNotificationsRealtime } from "../services/notifications-realtime.service";
import { useNotificationsStore } from "../store/notifications.store";

export function NotificationsRealtimeBridge() {
	const userId = useAuthStore((state) => state.user?.id);
	const userType = useAuthStore((state) => state.user?.userType ?? null);
	const {
		loadInitial,
		appendRealtime,
		catchUp,
		reset,
		setConnecting,
		setRealtimeStatus,
	} = useNotificationsStore();

	useEffect(() => {
		if (!userId) {
			reset();
			return;
		}

		let disconnected = false;
		let hasConnectedOnce = false;
		let cleanup: (() => void) | undefined;

		const start = async () => {
			setConnecting(true);
			setRealtimeStatus("connecting");
			try {
				await loadInitial();
				if (disconnected) {
					setConnecting(false);
					setRealtimeStatus("off");
					return;
				}

				const connection = await connectNotificationsRealtime({
					onNotification: (notification) =>
						appendRealtime(notification, userType),
					onConnected: () => {
						setConnecting(false);
						setRealtimeStatus("live");
						if (hasConnectedOnce) {
							void catchUp();
						}
						hasConnectedOnce = true;
					},
					onError: () => {
						setConnecting(false);
						setRealtimeStatus("off");
					},
				});

				if (disconnected) {
					connection.disconnect();
					return;
				}

				cleanup = connection.disconnect;
			} catch {
				// connectNotificationsRealtime throws when realtime is disabled
				// server-side, which is the default. That is "off", not an error
				// the operator needs to see as a failure.
				setConnecting(false);
				setRealtimeStatus("off");
			}
		};

		void start();

		return () => {
			disconnected = true;
			cleanup?.();
			reset();
		};
	}, [
		appendRealtime,
		catchUp,
		loadInitial,
		reset,
		setConnecting,
		setRealtimeStatus,
		userId,
		userType,
	]);

	return null;
}
